import { Component, EventEmitter, OnDestroy, Output, ViewChild, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { OpenScanService } from 'src/app/share/services/open-scan/open-scan.service';
import jsPDF from 'jspdf';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationPopupComponent } from 'src/app/share/popups/confirmation-popup/confirmation-popup.component';
import { CommonService } from 'src/app/share/services/common/common.service';
import { UploadStateService } from 'src/app/share/services/upload-state.service';

@Component({
    selector: 'app-open-scan',
    templateUrl: './open-scan.component.html',
    styleUrl: './open-scan.component.scss',
    standalone: false
})
export class OpenScanComponent implements OnDestroy {

    scanOptions = {
        IfShowUI: true,
        PixelType: "1",// "gray"
        Resolution: 200,
        IfFeederEnabled: false,
        duplex: false,
        IfDisableSourceAfterAcquire: true,
        IfGetImageInfo: false,
        IfGetExtImageInfo: false,
        extendedImageInfoQueryLevel: 0,
        PageSize: 1,
        colorMode: 'Color'
    };
    scannerList: any[] = [];
    selectedScannerId: string = '';
    errorMessage: string = '';
    scannedPages: { dataUrl: string, blob: Blob }[] = [];
    selectedPageIndex: number = -1;
    isLoading: boolean = false;
    draggedIndex: number | null = null;
    message: string = '';
    isSuccessToster: boolean = false;
    @ViewChild('previewPanel') previewPanel!: ElementRef;
    @ViewChildren('previewPage') previewPages!: QueryList<ElementRef>;
    private isAutoScrolling = false;
    private scrollTimeout: any;
    @Output() closeEvent: EventEmitter<any> = new EventEmitter<any>();

    constructor(private readonly openScanService: OpenScanService,
        private readonly modalService: NgbModal,
        private readonly commonService: CommonService,
        private readonly uploadStateService: UploadStateService
    ) {      
        this.uploadStateService.setScanning(true);
    }

    ngOnInit() {
        this.getScannerList();
    }

    ngOnDestroy() {
        this.uploadStateService.setScanning(false);
    }

    getScannerList() {
        this.openScanService.getScannerList().subscribe({
            next: (res: any) => {
                this.scannerList = res;
            },
            error: (error: any) => {
                this.errorMessage = error?.error?.error?.message;
                setTimeout(() => {
                    this.errorMessage = '';
                }, 5000);
            }
        });
    }

    selectPage(index: number) {
        this.selectedPageIndex = index;
        this.scrollToPage(index);
    }

    scrollToPage(index: number) {
        const pageId = 'page-' + index;
        const element = document.getElementById(pageId);
        if (element && this.previewPanel) {
            this.isAutoScrolling = true;
            const panel = this.previewPanel.nativeElement;
            const elementRect = element.getBoundingClientRect();
            const panelRect = panel.getBoundingClientRect();
            const scrollTop = element.offsetTop - 20;
            panel.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
            });
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = setTimeout(() => {
                this.isAutoScrolling = false;
            }, 500);
        }
    }

    onPreviewScroll(event: any) {
        if (this.isAutoScrolling) return;
        const panel = this.previewPanel.nativeElement;
        const panelRect = panel.getBoundingClientRect();
        const panelMidY = panelRect.top + panelRect.height / 2;
        let closestIndex = -1;
        let minDistance = Infinity;
        this.scannedPages.forEach((_, index) => {
            const pageId = 'page-' + index;
            const element = document.getElementById(pageId);
            if (element) {
                const rect = element.getBoundingClientRect();
                const elementMidY = rect.top + rect.height / 2;
                const distance = Math.abs(elementMidY - panelMidY);

                if (distance < minDistance) {
                    minDistance = distance;
                    closestIndex = index;
                }
            }
        });
        if (closestIndex !== -1 && closestIndex !== this.selectedPageIndex) {
            this.selectedPageIndex = closestIndex;
            this.scrollThumbnailIntoView(closestIndex);
        }
    }

    scrollThumbnailIntoView(index: number) {
        const thumbContainer = document.querySelector('.thumbnail-panel');
        if (thumbContainer) {
            const thumb = thumbContainer.children[0]?.children[index] as HTMLElement;
            if (thumb) {
                thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }

    scanDocument() {
        if (!this.selectedScannerId) {
            this.errorMessage = 'Please select a scanner first';
            setTimeout(() => {
                this.errorMessage = '';
            }, 5000);
            return;
        }
        this.isLoading = true;
        const payload = {
            scannerId: this.selectedScannerId,
            resolution: this.scanOptions.Resolution,
            paperSize: 'A4',
            colorMode: this.scanOptions.colorMode,
            paperSource: 'Auto',
            brightness: 0,
            contrast: 0,
            IfFeederEnabled: this.scanOptions.IfFeederEnabled,
            IfDuplexEnabled: this.scanOptions.duplex
        };
        this.openScanService.scanPdf(this.selectedScannerId, payload).subscribe({
            next: (res: Blob) => {
                this.isLoading = false;
                this.processScannedResult(res);
            },
            error: (error: any) => {
                this.isLoading = false;
                this.errorMessage = error?.error?.error?.message || 'Scan failed';
                setTimeout(() => {
                    this.errorMessage = '';
                }, 5000);
            }
        });
    }

    processScannedResult(blob: Blob) {
        const reader = new FileReader();
        reader.onload = (event: any) => {
            const dataUrl = event.target.result;
            this.scannedPages.push({ dataUrl, blob });
            this.selectedPageIndex = this.scannedPages.length - 1;
            setTimeout(() => {
                this.scrollToPage(this.selectedPageIndex);
            }, 100);
        };
        reader.readAsDataURL(blob);
    }

    getImageDimensions(dataUrl: string): Promise<{ width: number, height: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                resolve({ width: img.width, height: img.height });
            };
            img.onerror = (e) => reject(e);
            img.src = dataUrl;
        });
    }

    async uploadToPdf() {
        if (this.scannedPages.length === 0) {
            this.errorMessage = 'Please scan a document first';
            setTimeout(() => {
                this.errorMessage = '';
            }, 5000);
            return;
        }
        this.isLoading = true;
        try {
            const pdfBlob = await this.generatePdfBlob();
            let newFile = new File([pdfBlob], 'Scan_' + Date.parse(new Date().toString()) + '.pdf', { type: pdfBlob.type });
            let saveResults = {
                blob: [newFile],
                blobURL: [URL.createObjectURL(newFile)],
                base64String: [],
                savedFiles: [],
                uploadedFiles: [],
                base64ButtonText: [],
                saveFileText: [],
                blobToShow: null
            };
            this.commonService.setScanFiles(saveResults);
            this.closeEvent.emit();
            this.errorMessage = '';
            this.isLoading = false;
        } catch (e) {
            console.error('Error generating PDF', e);
            this.errorMessage = 'Failed to generate PDF';
            this.isLoading = false;
            setTimeout(() => {
                this.errorMessage = '';
            }, 5000);
        }
    }

    async generatePdfBlob(): Promise<Blob> {
        const pdf = new jsPDF({
            orientation: 'p',
            unit: 'mm',
            format: 'a4',
            compress: true
        });
        for (let i = 0; i < this.scannedPages.length; i++) {
            const page = this.scannedPages[i];
            const dims = await this.getImageDimensions(page.dataUrl);
            const orientation = dims.width > dims.height ? 'l' : 'p';
            if (i > 0) {
                pdf.addPage('a4', orientation);
            }
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth - 10;
            const imgHeight = (dims.height * imgWidth) / dims.width;
            pdf.addImage(page.dataUrl, 'JPEG', 5, 5, imgWidth, imgHeight, undefined, 'FAST');
        }
        return pdf.output('blob');
    }

    onDragStart(event: DragEvent, index: number) {
        this.draggedIndex = index;
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', index.toString());
        }
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
    }

    onDrop(event: DragEvent, dropIndex: number) {
        event.preventDefault();
        if (this.draggedIndex === null || this.draggedIndex === dropIndex) {
            this.draggedIndex = null;
            return;
        }
        const movedItem = this.scannedPages.splice(this.draggedIndex, 1)[0];
        this.scannedPages.splice(dropIndex, 0, movedItem);
        this.selectedPageIndex = dropIndex;
        setTimeout(() => {
            this.scrollToPage(dropIndex);
        }, 50);

        this.draggedIndex = null;
    }

    onDragEnd() {
        this.draggedIndex = null;
    }

    deletePage(index: number, event: MouseEvent) {
        event.stopPropagation();
        const modalRef = this.modalService.open(ConfirmationPopupComponent, {
            centered: true,
            backdrop: 'static',
            keyboard: false
        });
        modalRef.componentInstance.header = 'Delete Page';
        modalRef.componentInstance.message = 'Are you sure you want to delete this page';
        modalRef.componentInstance.lableName = 'Delete';
        modalRef.componentInstance.lableCancel = 'Cancel';
        modalRef.componentInstance.isConfirmationMessage = false;
        modalRef.componentInstance.showQuestionMark = true;
        modalRef.componentInstance.showCancelButton = true;
        modalRef.result.then(
            (result: any) => {
                if (result === true || result?.isClose) {
                    this.performDelete(index);
                }
            },
            () => {
                event.stopPropagation();
            }
        );
    }

    performDelete(index: number) {
        this.scannedPages.splice(index, 1);

        if (this.scannedPages.length === 0) {
            this.selectedPageIndex = -1;
        } else {
            this.selectedPageIndex = Math.min(index, this.scannedPages.length - 1);
        }
    }

    async saveAsPdf() {
        if (!this.scannedPages.length) {
            this.errorMessage = '"There is no image to save!"';
            setTimeout(() => {
                this.errorMessage = '';
            }, 5000);
            return;
        }
        try {
            const pdfBlob = await this.generatePdfBlob();
            const handle = await (window as any).showSaveFilePicker({
                suggestedName: "ScannedDocument.pdf",
                types: [
                    {
                        description: "PDF File",
                        accept: { "application/pdf": [".pdf"] }
                    }
                ]
            });
            const writable = await handle.createWritable();
            await writable.write(pdfBlob);
            await writable.close();
            this.message = "PDF saved successfully!";
            this.isSuccessToster = true;
        } catch (err: any) {
            console.error(err);
        }
    }

    closeToster(value: boolean) {
        this.isSuccessToster = value;
    }

}
